import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import OrgItem from "@/components/modal-dark/modal-dark-platform-content/choose-portal/OrgItem"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useOrganizationsGeneralInfoQuery } from "@/graphql"
import { OutgoingOnlyPortalContent } from "@/helpers/orgSession"
import { websiteRoutes } from "@/router/routes"
import { EASETECH, WAYLEAVE } from "@/utilities/global-variables"
import { Col, Row, Skeleton } from "antd"
import { FC } from "react"
import { useNavigate } from "react-router-dom"
import "../choose-portal/ChoosePortal.less"
import "./OutgoingOnlyPortal.less"

const { Title, Paragraph } = WebsiteTypography

const SUPPORT_EMAIL = "support@easetech.co.za"

const capitalize = (slug: string) => slug.charAt(0).toUpperCase() + slug.slice(1)

/**
 * Shown to a client who tries to sign in to an outgoing-only organization's portal (the CMS refuses
 * the session) or still holds an older session there (DashboardLayout signs it out). Such a portal
 * serves that organization's agents only, so the client is pointed to the Easetech portal, where they
 * can apply to this organization among many others.
 */
const OutgoingOnlyPortal: FC = () => {
  const navigate = useNavigate()
  const { website } = websiteRoutes
  const { modalDarkContent, setIsModalDarkOpen } = useModalDarkContext()
  const { orgSlug } = (modalDarkContent ?? {}) as OutgoingOnlyPortalContent

  const { data, loading } = useOrganizationsGeneralInfoQuery({
    variables: { filter: { slug: { in: [orgSlug, EASETECH] } } },
    skip: !orgSlug,
  })

  const organizations = (data?.organizations?.data ?? []) as OrganizationEntity[]
  const easetechOrg = organizations.find(it => it?.attributes?.slug === EASETECH)
  const orgName =
    organizations.find(it => it?.attributes?.slug === orgSlug)?.attributes?.name || capitalize(orgSlug ?? "")

  const onGoToEasetech = () => {
    setIsModalDarkOpen(false)
    navigate(`/${EASETECH}${website.auth}/${WAYLEAVE}/login`)
  }

  return (
    <Row justify={"center"} className={"choose-portal-wrapper outgoing-only-portal-wrapper"} style={{ paddingTop: 24, paddingBottom: 24 }}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Title level={3} centered className={"section-title"}>
          <span className={"w300"}>{"Portal for "}</span>
          <span className={"bold"}>{`${orgName} Agents`}</span>
        </Title>

        <Row justify={"center"}>
          <Col span={18} xxl={18} xl={18} lg={18} md={22} sm={24} xs={24}>
            <Paragraph
              centered
              size={"md"}
              style={{ paddingTop: 24, paddingBottom: 24 }}
              color={"website-secondary"}
              weight={"w300"}
            >
              {`The ${orgName} portal is available to ${orgName} agents only and does not accept applications submitted directly through it, so client accounts can't sign in here.\n` +
                "To create an application, please use the Easetech portal instead."}
            </Paragraph>
          </Col>
        </Row>

        <Row style={{ marginBottom: 24 }}>
          <Col span={24}>
            {loading ? (
              <Skeleton loading paragraph={{ rows: 2 }} />
            ) : easetechOrg ? (
              <>
                <OrgItem it={easetechOrg} onClick={onGoToEasetech} />
                <Paragraph centered size={"sm"} color={"website-secondary"} weight={"w300"} style={{ paddingTop: 8 }}>
                  {"Go to the Easetech portal"}
                </Paragraph>
              </>
            ) : (
              <Row justify={"center"}>
                <Button btnType={"primary"} text={"GO TO EASETECH PORTAL"} onClick={onGoToEasetech} />
              </Row>
            )}
          </Col>
        </Row>
      </Col>

      <Row justify={"center"} className={"divider-with-hex"} style={{ margin: "0 0 24px 0" }}>
        <div className={"hex-small"} />
      </Row>

      <Row justify={"center"}>
        <Col span={18} xxl={18} xl={18} lg={18} md={22} sm={24} xs={24}>
          <Paragraph weight={"w300"} size={"sm"} color={"website-secondary"} centered>
            {`On the Easetech portal you can apply to many other organizations, including ${orgName}. If you don't have an Easetech account yet, you can register there with the same email address.`}
          </Paragraph>
          <Paragraph weight={"w300"} size={"sm"} color={"website-secondary"} centered style={{ paddingTop: 16 }}>
            {`If your role is expected to be Agent, please contact Easetech support and we will resolve it as soon as possible: `}
            <a href={`mailto:${SUPPORT_EMAIL}`} className={"support-email-link"}>{SUPPORT_EMAIL}</a>
          </Paragraph>
        </Col>
      </Row>
    </Row>
  )
}

export default OutgoingOnlyPortal
